import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';
import { toast } from 'react-toastify';
import config from '../config';

const VenueFeaturesManager = ({ venueId }) => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { setActiveTabId } = useApplicationContext();
    
    const [features, setFeatures] = useState([]);
    const [limits, setLimits] = useState({ max: 0, current: 0 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    
    // New feature form
    const [newFeature, setNewFeature] = useState({
        name: '',
        description: '',
        price: ''
    });
    const [showAddForm, setShowAddForm] = useState(false);

    // Fetch features
    const fetchFeatures = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/features`);
            const data = await response.json();
            
            if (response.ok) {
                // Backend now returns { features: [], limit: N }
                // Handle both new format and potential legacy format (just array)
                const featureList = Array.isArray(data) ? data : (data.features || []);
                const limitValue = !Array.isArray(data) && data.limit !== undefined ? data.limit : 0;

                setFeatures(featureList);
                setLimits({
                    max: limitValue,
                    current: featureList.length
                });
            }
        } catch (error) {
            console.error('Error fetching features:', error);
        } finally {
            setLoading(false);
        }
    }, [auth, venueId]);

    useEffect(() => {
        if (venueId) {
            fetchFeatures();
        }
    }, [venueId, fetchFeatures]);

    // Get remaining slots
    const getRemainingSlots = () => {
        return Math.max(0, limits.max - limits.current);
    };

    const handleUpgrade = (e) => {
        if (e) e.preventDefault();
        setActiveTabId('my-plan');
        navigate('/applications');
    };

    // Handle new feature input
    const handleNewFeatureChange = (e) => {
        const { name, value } = e.target;
        setNewFeature(prev => ({ ...prev, [name]: value }));
    };

    // Add new feature
    const handleAddFeature = async (e) => {
        e.preventDefault();
        
        if (!newFeature.name.trim()) {
            toast.error('Feature name is required');
            return;
        }
        
        if (getRemainingSlots() <= 0) {
            toast.error('You have reached the maximum number of features allowed');
            return;
        }
        
        setSaving(true);
        
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/features`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newFeature.name,
                    description: newFeature.description,
                    price: newFeature.price ? parseFloat(newFeature.price) : null
                })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                toast.success('Feature added successfully');
                setNewFeature({ name: '', description: '', price: '' });
                setShowAddForm(false);
                fetchFeatures();
            } else {
                if (data.error === 'SUBSCRIPTION_LIMIT_EXCEEDED') {
                    toast.error(data.message);
                } else {
                    toast.error(data.message || 'Failed to add feature');
                }
            }
        } catch (error) {
            console.error('Error adding feature:', error);
            toast.error('Failed to add feature');
        } finally {
            setSaving(false);
        }
    };

    // Update feature
    const handleUpdateFeature = async (feature) => {
        setSaving(true);
        
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/features/${feature.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: feature.name,
                    description: feature.description,
                    price: feature.price ? parseFloat(feature.price) : null
                })
            });
            
            if (response.ok) {
                toast.success('Feature updated successfully');
                setEditingId(null);
                fetchFeatures();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to update feature');
            }
        } catch (error) {
            console.error('Error updating feature:', error);
            toast.error('Failed to update feature');
        } finally {
            setSaving(false);
        }
    };

    // Delete feature
    const handleDeleteFeature = async (featureId) => {
        if (!window.confirm('Are you sure you want to delete this feature?')) return;
        
        setDeletingId(featureId);
        
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/features/${featureId}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                toast.success('Feature deleted successfully');
                fetchFeatures();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to delete feature');
            }
        } catch (error) {
            console.error('Error deleting feature:', error);
            toast.error('Failed to delete feature');
        } finally {
            setDeletingId(null);
        }
    };

    // Edit feature inline
    const handleFeatureEdit = (featureId, field, value) => {
        setFeatures(prev => prev.map(f => 
            f.id === featureId ? { ...f, [field]: value } : f
        ));
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-48">
                <div className="spinner-border text-primary" />
            </div>
        );
    }

    return (
        <div className="card radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-12">
                    <h6 className="text-lg fw-semibold mb-0">
                        <Icon icon="mdi:format-list-checks" className="me-8" />
                        Venue Features
                    </h6>
                    <div className="d-flex align-items-center gap-12">
                        <span className="hs-badge hs-badge-info">
                            {limits.max === 0 ? 'Not included in plan' : `${limits.current} / ${limits.max} features`}
                        </span>
                        {getRemainingSlots() > 0 && !showAddForm && (
                            <button
                                className="btn btn-primary btn-sm radius-8"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Icon icon="mdi:plus" className="me-4" />
                                Add Feature
                            </button>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="card-body p-24">
                {/* Add Feature Form */}
                {showAddForm && getRemainingSlots() > 0 && (
                    <form onSubmit={handleAddFeature} className="bg-neutral-50 radius-12 p-16 mb-24">
                        <div className="row g-12">
                            <div className="col-md-4">
                                <label className="form-label text-sm fw-medium">
                                    Feature Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control radius-8"
                                    name="name"
                                    value={newFeature.name}
                                    onChange={handleNewFeatureChange}
                                    placeholder="e.g., Free WiFi"
                                    maxLength={100}
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-sm fw-medium">Description</label>
                                <input
                                    type="text"
                                    className="form-control radius-8"
                                    name="description"
                                    value={newFeature.description}
                                    onChange={handleNewFeatureChange}
                                    placeholder="Brief description"
                                    maxLength={255}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label text-sm fw-medium">Price (AED)</label>
                                <input
                                    type="number"
                                    className="form-control radius-8"
                                    name="price"
                                    value={newFeature.price}
                                    onChange={handleNewFeatureChange}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                            <div className="col-md-2 d-flex align-items-end gap-8">
                                <button
                                    type="submit"
                                    className="btn btn-primary radius-8 flex-grow-1"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                        <Icon icon="mdi:check" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary radius-8"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setNewFeature({ name: '', description: '', price: '' });
                                    }}
                                >
                                    <Icon icon="mdi:close" />
                                </button>
                            </div>
                        </div>
                    </form>
                )}
                
                {/* Limit Warning / Not Available */}
                {limits.max === 0 ? (
                    <div className="alert alert-secondary mb-24 bg-neutral-100 border-neutral-200 text-neutral-600">
                        <div className="d-flex align-items-center">
                            <Icon icon="mdi:lock" className="text-xl me-12" />
                            <div>
                                <p className="fw-semibold mb-0">Venue Features Not Available</p>
                                <p className="text-sm mb-0">
                                    Your current plan does not include custom venue features. 
                                    <button type="button" onClick={handleUpgrade} className="ms-4 text-primary-600 fw-medium p-0 border-0 bg-transparent text-decoration-underline">Upgrade to add this feature</button>
                                </p>
                            </div>
                        </div>
                    </div>
                ) : getRemainingSlots() <= 0 && !showAddForm && (
                    <div className="alert alert-warning mb-24 d-flex align-items-center">
                        <Icon icon="mdi:alert" className="me-12 text-xl" />
                        <div>
                            You have reached the maximum number of features ({limits.max}) allowed by your subscription plan.
                            <button type="button" onClick={handleUpgrade} className="p-0 border-0 bg-transparent ms-8 fw-semibold text-decoration-underline">Upgrade your plan</button>
                        </div>
                    </div>
                )}
                
                {/* Features List */}
                {features.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="bg-neutral-100">
                                <tr>
                                    <th className="text-sm fw-semibold">Feature Name</th>
                                    <th className="text-sm fw-semibold">Description</th>
                                    <th className="text-sm fw-semibold text-end">Price</th>
                                    <th className="text-sm fw-semibold text-center" style={{ width: '120px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {features.map((feature) => (
                                    <tr key={feature.id}>
                                        <td>
                                            {editingId === feature.id ? (
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm radius-8"
                                                    value={feature.name}
                                                    onChange={(e) => handleFeatureEdit(feature.id, 'name', e.target.value)}
                                                    maxLength={100}
                                                />
                                            ) : (
                                                <span className="fw-medium">{feature.name}</span>
                                            )}
                                        </td>
                                        <td>
                                            {editingId === feature.id ? (
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm radius-8"
                                                    value={feature.description || ''}
                                                    onChange={(e) => handleFeatureEdit(feature.id, 'description', e.target.value)}
                                                    maxLength={255}
                                                />
                                            ) : (
                                                <span className="text-secondary-light">{feature.description || '-'}</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            {editingId === feature.id ? (
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm radius-8"
                                                    value={feature.price || ''}
                                                    onChange={(e) => handleFeatureEdit(feature.id, 'price', e.target.value)}
                                                    step="0.01"
                                                    min="0"
                                                    style={{ width: '100px', marginLeft: 'auto' }}
                                                />
                                            ) : (
                                                <span className={feature.price ? 'text-success-600 fw-medium' : 'text-secondary-light'}>
                                                    {feature.price ? `AED ${parseFloat(feature.price).toFixed(2)}` : 'Free'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-8">
                                                {editingId === feature.id ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                            onClick={() => handleUpdateFeature(feature)}
                                                            disabled={saving}
                                                        >
                                                            {saving ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                <Icon icon="mdi:check" className="text-lg" />
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="w-32-px h-32-px bg-neutral-200 text-neutral-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                            onClick={() => {
                                                                setEditingId(null);
                                                                fetchFeatures();
                                                            }}
                                                        >
                                                            <Icon icon="mdi:close" className="text-lg" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                            onClick={() => setEditingId(feature.id)}
                                                            title="Edit"
                                                        >
                                                            <Icon icon="mdi:pencil" className="text-lg" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="w-32-px h-32-px bg-danger-50 text-danger-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                            onClick={() => handleDeleteFeature(feature.id)}
                                                            disabled={deletingId === feature.id}
                                                            title="Delete"
                                                        >
                                                            {deletingId === feature.id ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                <Icon icon="mdi:delete" className="text-lg" />
                                                            )}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-48 bg-neutral-50 radius-12">
                        <Icon icon="mdi:format-list-bulleted" className="text-5xl text-secondary-light mb-12" />
                        <p className="text-secondary-light mb-8">No features added yet</p>
                        {getRemainingSlots() > 0 && (
                            <button
                                className="btn btn-primary btn-sm radius-8"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Icon icon="mdi:plus" className="me-4" />
                                Add First Feature
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VenueFeaturesManager;
